import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Col } from "reactstrap";
import Logo from "../../assets/images/LogoNew.svg";
import {
  InputField
} from "../../components";
import LanguagePicker from "../../components/LanguagePicker/LanguagePicker";
import Timer from "../../components/Timer";
import { DialogContext } from "../../store/context/DialogContext";
import { useTranslation } from "../../store/context/TranslationContext";
import { UserContext } from "../../store/context/UserContext";
import { doGET, doPOST } from "../../util/HttpUtil";
import {
  clearAuthResume,
  clearStoredAuthMessage,
  getValidatedResumePathForApplication,
  rememberApplicationLoginPathForUrl,
  setActiveAuthApplicationId,
  setLegacyUnauthUrl,
} from "../../util/authResume";
import { handleUtmLoginSuccess } from "../../util/utm";
import OtpInput from "./OtpInput";
import ButtonGroupNew from "../Form/forms/BusinessLiteForms/components/ButtonGroupNew";
import EddWorkableRequestsScreen from "./EddWorkableRequestsScreen";
import WorkableApplicationsScreen, {
  getApplicationTestKey,
  getCreateTestKey,
} from "./WorkableApplicationsScreen";
import {
  createV2,
  eddLoginV2,
  eddResumeV2,
  hasFinalSessionTokens,
  loginV2,
  persistFinalAuthResponse,
  resumeV2,
} from "./authV2Service";
import {
  LOGIN_V2_DECISION,
  buildLoginV2Decision,
  resolveCreatePayloadForCurrentRoute,
} from "./loginV2Decision";
import {
  buildApplicationLoginData,
  buildCreateQueryString,
  buildFinalAuthNavigationTarget,
  buildLegacyCreateRequest,
  buildV2BlockedMessage,
  getLoginApplicationId,
  isBlockedLoginV2Decision,
  isUnifiedBusinessLoginForm,
  resolveBusinessRouteInfo,
  resolveUnifiedBusinessFormType,
  resolveUnifiedBusinessType,
  shouldUseV2LoginForForm,
} from "./loginV2Flow";
import {
  buildEddBlockedMessage,
  buildEddLoginData,
  buildEddLoginV2Decision,
  buildFinalEddAuthNavigationTarget,
  getEddRequestTestKey,
} from "./loginV2EddFlow";
import { buildAutomationChildTestId } from "../../components/InputField/testId";

const LoginOtp = ({
  data,
  setData,
  loading,
  form,
  setStep,
  otpId,
  setLoading,
  testId,
}) => {
  const { login, userContent, populateUserContent } = useContext(UserContext);

  let navigate = useNavigate();
  const { showError } = useContext(DialogContext);
  const { t } = useTranslation();
  const [isDisabled, setIsDisabled] = useState(true);
  const [v2Selection, setV2Selection] = useState(null);
  const [v2ActionLoadingKey, setV2ActionLoadingKey] = useState("");
  const [v2Error, setV2Error] = useState("");
  const [eddSelection, setEddSelection] = useState(null);
  const [eddActionLoadingKey, setEddActionLoadingKey] = useState("");
  const [eddError, setEddError] = useState("");
  const otpTestId = buildAutomationChildTestId(testId, "otp");

  const isUnifiedBusinessLogin = isUnifiedBusinessLoginForm(form);
  const shouldUseV2Login = shouldUseV2LoginForForm(form);

  useEffect(() => {
    setTimeout(() => {
      setIsDisabled(false);
    }, 30000);
  }, []);

  const navigationWrapper = ({ url, config, formType, utmPayload }) => {
    if (form?.path === "business-plus" && form?.isPrime) {
      rememberApplicationLoginPathForUrl({
        url,
        loginPath: "/business-prime-plus/login",
      });
    }
    clearStoredAuthMessage();
    clearAuthResume();
    setLegacyUnauthUrl("");
    handleUtmLoginSuccess({ utmPayload, formType })
    navigate(url, config)
  }

  const navigateWithFinalAuthResponse = async ({
    response,
    applicationOption = {},
    fallbackFormType,
    fallbackType,
    requireSessionTokens = true,
  }) => {
    await persistFinalAuthResponse(response, populateUserContent, {
      requireSessionTokens,
      activeApplicationId: applicationOption?.applicationId || applicationOption?.application_id,
    });
    const navigationTarget = buildFinalAuthNavigationTarget({
      response,
      applicationOption,
      fallbackFormType,
      fallbackType,
      form,
    });
    if (!navigationTarget) {
      return false;
    }

    navigationWrapper({
      url: navigationTarget.url,
      config: navigationTarget.config,
      formType: navigationTarget.formType,
      utmPayload: data?.gTagUtmData,
    });
    return true;
  };

  const createLegacyApplicationAfterV2Auth = async ({
    selectedCreateOption,
    unifiedBusinessType,
    unifiedBusinessFormType,
  }) => {
    if (!form?.apiPath) {
      throw new Error("Application id was not returned for this login.");
    }

    const { endpoint, payload: createPayload, selectedType } = buildLegacyCreateRequest({
      data,
      form,
      selectedCreateOption,
      unifiedBusinessType,
    });
    const userFormCreateResponse = await doPOST(
      endpoint,
      createPayload
    );

    if (!userFormCreateResponse?.data?._id) {
      throw new Error("Application could not be created.");
    }
    setActiveAuthApplicationId(userFormCreateResponse.data._id);

    const createdRouteInfo = isUnifiedBusinessLogin
      ? await resolveBusinessRouteInfo({
        applicationId: userFormCreateResponse?.data?._id,
        fallbackType: selectedType,
        form,
      })
      : null;
    navigationWrapper({
      url: createdRouteInfo?.url || `/${form?.path}/${userFormCreateResponse?.data?._id}`,
      config: { replace: true, ...(createdRouteInfo?.state && { state: createdRouteInfo.state }) },
      formType: createdRouteInfo?.formType || unifiedBusinessFormType || form?.formType,
      utmPayload: data?.gTagUtmData,
    });
  };

  const resumeApplicationWithV2 = async ({ actionToken, applicationOption, unifiedBusinessFormType, unifiedBusinessType }) => {
    const applicationId = applicationOption?.applicationId || applicationOption?.application_id;
    const response = await resumeV2(actionToken, applicationId);
    await navigateWithFinalAuthResponse({
      response,
      applicationOption,
      fallbackFormType: unifiedBusinessFormType || form?.formType,
      fallbackType: unifiedBusinessType,
    });
  };

  const createApplicationWithV2 = async ({ actionToken, selectedCreateOption, unifiedBusinessFormType, unifiedBusinessType }) => {
    const createPayload = resolveCreatePayloadForCurrentRoute(form, selectedCreateOption);
    if (!createPayload) {
      throw new Error("No create option is available for this login route.");
    }

    const response = await createV2(actionToken, createPayload);
    const createdApplicationId = getLoginApplicationId(response);
    const didNavigate = await navigateWithFinalAuthResponse({
      response,
      applicationOption: {
        application_id: createdApplicationId,
        formType: response?.data?.userContent?.formType || createPayload?.formType,
        applicationFormType: response?.data?.userContent?.formType || createPayload?.formType,
        type: response?.data?.userContent?.type ?? createPayload?.type,
      },
      fallbackFormType: createPayload?.formType || unifiedBusinessFormType || form?.formType,
      fallbackType: createPayload?.type ?? unifiedBusinessType,
      requireSessionTokens: Boolean(createdApplicationId),
    });

    if (!didNavigate) {
      await createLegacyApplicationAfterV2Auth({
        selectedCreateOption: selectedCreateOption || createPayload,
        unifiedBusinessType: createPayload?.type ?? unifiedBusinessType,
        unifiedBusinessFormType: createPayload?.formType || unifiedBusinessFormType,
      });
    }
  };

  const handleV2ResumeSelection = async (applicationOption) => {
    if (!v2Selection?.actionToken) {
      setV2Error("Your secure login session has expired. Please restart login.");
      return;
    }

    const actionKey = `resume:${getApplicationTestKey(applicationOption)}`;
    setV2ActionLoadingKey(actionKey);
    setLoading(true);
    setV2Error("");
    try {
      await resumeApplicationWithV2({
        actionToken: v2Selection.actionToken,
        applicationOption,
        unifiedBusinessFormType: v2Selection.unifiedBusinessFormType,
        unifiedBusinessType: v2Selection.unifiedBusinessType,
      });
    } catch (error) {
      setV2Error(error?.message || "Unable to resume this application.");
      showError(error);
    } finally {
      setV2ActionLoadingKey("");
      setLoading(false);
    }
  };

  const handleV2CreateSelection = async (selectedCreateOption) => {
    if (!v2Selection?.actionToken) {
      setV2Error("Your secure login session has expired. Please restart login.");
      return;
    }

    const actionKey = `create:${getCreateTestKey(selectedCreateOption)}`;
    setV2ActionLoadingKey(actionKey);
    setLoading(true);
    setV2Error("");
    try {
      await createApplicationWithV2({
        actionToken: v2Selection.actionToken,
        selectedCreateOption,
        unifiedBusinessFormType: v2Selection.unifiedBusinessFormType,
        unifiedBusinessType: v2Selection.unifiedBusinessType,
      });
    } catch (error) {
      setV2Error(error?.message || "Unable to start this application.");
      showError(error);
    } finally {
      setV2ActionLoadingKey("");
      setLoading(false);
    }
  };

  const handleV2ChooserBack = () => {
    setV2Selection(null);
    setV2Error("");
    setV2ActionLoadingKey("");
    setStep(0);
  };

  const onLoginV2 = async () => {
    if (loading) return;
    setLoading(true);
    setV2Error("");
    try {
      const {
        loginData,
        unifiedBusinessType,
        unifiedBusinessFormType,
      } = buildApplicationLoginData({ data, form, otpId });
      const response = await loginV2(loginData);
      const actionToken = response?.data?.actionToken;
      if (!actionToken) {
        throw new Error("Login response is missing action token.");
      }

      const decision = buildLoginV2Decision({
        applications: response?.data?.applications,
        createOptions: response?.data?.createOptions,
        form,
      });
      const nextSelection = {
        actionToken,
        decision,
        email: response?.data?.email || loginData.email,
        applications: response?.data?.applications || [],
        createOptions: response?.data?.createOptions || [],
        unifiedBusinessType,
        unifiedBusinessFormType,
      };

      if (decision.type === LOGIN_V2_DECISION.AUTO_RESUME) {
        await resumeApplicationWithV2({
          actionToken,
          applicationOption: decision.selectedApplication,
          unifiedBusinessFormType,
          unifiedBusinessType,
        });
        return;
      }

      if (decision.type === LOGIN_V2_DECISION.AUTO_CREATE) {
        await createApplicationWithV2({
          actionToken,
          selectedCreateOption: decision.selectedCreateOption,
          unifiedBusinessFormType,
          unifiedBusinessType,
        });
        return;
      }

      if (isBlockedLoginV2Decision(decision)) {
        setV2Error(buildV2BlockedMessage(decision));
      }

      setV2Selection(nextSelection);
    } catch (error) {
      setV2Selection(null);
      showError(error);
      setStep(0);
    } finally {
      setLoading(false);
    }
  };

  const navigateWithFinalEddAuthResponse = async ({
    response,
    eddRequestOption = {},
  }) => {
    const responseApplicationId = response?.data?.userContent?.application_id || eddRequestOption?.applicationId;

    await persistFinalAuthResponse(response, populateUserContent, {
      requireSessionTokens: hasFinalSessionTokens(response),
      activeApplicationId: responseApplicationId,
    });

    const navigationTarget = buildFinalEddAuthNavigationTarget({
      response,
      eddRequestOption,
    });

    if (!navigationTarget) {
      throw new Error("Periodic review session opened, but the destination route could not be resolved.");
    }

    navigationWrapper({
      url: navigationTarget.url,
      config: navigationTarget.config,
      formType: navigationTarget.formType,
      utmPayload: data?.gTagUtmData,
    });
  };

  const resumeEddRequestWithV2 = async ({ actionToken, eddRequestOption }) => {
    const response = await eddResumeV2(actionToken, eddRequestOption?.eddRequestId);
    await navigateWithFinalEddAuthResponse({
      response,
      eddRequestOption,
    });
  };

  const handleEddV2ResumeSelection = async (eddRequestOption) => {
    if (!eddSelection?.actionToken) {
      setEddError("Your secure login session has expired. Please sign in again.");
      return;
    }

    const actionKey = `resume-edd:${getEddRequestTestKey(eddRequestOption)}`;
    setEddActionLoadingKey(actionKey);
    setLoading(true);
    setEddError("");
    try {
      await resumeEddRequestWithV2({
        actionToken: eddSelection.actionToken,
        eddRequestOption,
      });
    } catch (error) {
      setEddError(error?.message || "Unable to open this periodic review.");
      showError(error);
    } finally {
      setEddActionLoadingKey("");
      setLoading(false);
    }
  };

  const handleEddV2ChooserBack = () => {
    setEddSelection(null);
    setEddError("");
    setEddActionLoadingKey("");
    setStep(0);
  };

  const handleEddLogin = async () => {
    if (loading) return;
    setLoading(true);
    setEddError("");
    try {
      const loginData = buildEddLoginData({ data, otpId });
      if (!loginData.email) {
        throw new Error("Email is required to continue.");
      }

      const response = await eddLoginV2(loginData);
      const actionToken = response?.data?.actionToken;
      if (!actionToken) {
        throw new Error("Login response is missing action token.");
      }

      const decision = buildEddLoginV2Decision({
        eddRequests: response?.data?.eddRequests,
      });
      const nextSelection = {
        actionToken,
        decision,
        email: response?.data?.email || loginData.email,
        eddRequests: response?.data?.eddRequests || [],
      };

      if (decision.type === LOGIN_V2_DECISION.AUTO_RESUME) {
        await resumeEddRequestWithV2({
          actionToken,
          eddRequestOption: decision.selectedEddRequest,
        });
        return;
      }

      if (decision.type === LOGIN_V2_DECISION.BLOCKED) {
        setEddError(buildEddBlockedMessage(decision));
      }

      setEddSelection(nextSelection);
    } catch (error) {
      setEddSelection(null);
      showError(error);
      setStep(0);
    } finally {
      setLoading(false);
    }
  };

  const onLogin = async () => {
    if (shouldUseV2Login) {
      await onLoginV2();
      return;
    }

    if (loading) return;
    // if (!isTermsSelected) {
    //   showError("Please Accept Terms & Conditions");
    //   return;
    // }
    setLoading(true)
    const unifiedBusinessType = resolveUnifiedBusinessType({ form });
    const unifiedBusinessFormType = resolveUnifiedBusinessFormType({
      form,
      unifiedBusinessType,
      isUnifiedBusinessLogin,
    });
    try {
      let loginData = {};
      loginData.otpId = otpId;
      // loginData.userType = isCpanelLogin ? "superAdmin" : "supervisor";
      loginData.userType = "customer";
      if (data?.email) {
        loginData.email = data.email;
      } else {
        console.error(
          "Both email and phone are provided. Please provide either email or phone, not both."
        );
        return;
      }

      loginData.otpValue = data.otpValue;

      if (form?.formType) {
        loginData.formType = unifiedBusinessFormType;
        loginData.loginType = "APPLICATION_LOGIN";
      }
      if (isUnifiedBusinessLogin) {
        loginData.type = unifiedBusinessType;
      }

      if (data?.gTagUtmData) {
        loginData = { ...(loginData ?? {}), ...(data?.gTagUtmData ?? {}) };
      }

      if (data?.utm_referrer) {
        loginData = { ...(loginData ?? {}), utm_referrer: data?.utm_referrer, };
      }

      if (form?.type == "kyc") {
        loginData.loginType = "UBO_KYC_LOGIN";
        loginData.ubo_id = data?.ubo_id;
        if (data?.application_id) {
          loginData.application_id = data.application_id;
        }
      }

      var response = await login(loginData);
      if (response?.status == 200) {
        const responseApplicationId = getLoginApplicationId(response);
        // Broker "complete-application" logins carry an explicit redirectPath
        // (/complete-application/<id>/dashboard). Skip the generic stored resume
        // path (which resolves to /verify-individual/dashboard for kyc forms) so
        // the neutral broker route is honored instead.
        const resumePath = (isUnifiedBusinessLogin || form?.completeApplication)
          ? ""
          : getValidatedResumePathForApplication({
            form,
            applicationId: responseApplicationId,
          });
        if (resumePath) {
          navigationWrapper({
            url: resumePath,
            config: { replace: true },
            formType: form?.formType,
            utmPayload: data?.gTagUtmData
          });
          return;
        }
        if (form?.redirectPath) {
          navigationWrapper({ url: `${form?.redirectPath}`, config: { replace: true }, formType: form?.formType, utmPayload: data?.gTagUtmData })
        } else if (isUnifiedBusinessLogin) {
          const applicationId = response?.data?.userContent?.application_id || response?.data?.userContent?._id;
          const routeInfo = await resolveBusinessRouteInfo({
            applicationId,
            fallbackType: unifiedBusinessType,
            form,
          });
          if (routeInfo?.url) {
            navigationWrapper({
              url: routeInfo.url,
              config: { replace: true, state: routeInfo.state },
              formType: routeInfo.formType || unifiedBusinessFormType,
              utmPayload: data?.gTagUtmData
            });
          } else {
            const userFormCreateResponse = await doPOST(
              `/api/${form?.apiPath}/create?${buildCreateQueryString(data)}`,
              {
                email: data.email,
                type: unifiedBusinessType,
              }
            );
            if (userFormCreateResponse?.data?._id) {
              const createdRouteInfo = await resolveBusinessRouteInfo({
                applicationId: userFormCreateResponse?.data?._id,
                fallbackType: unifiedBusinessType,
                form,
              });
              navigationWrapper({
                url: createdRouteInfo?.url || `/${form?.path}/${userFormCreateResponse?.data?._id}`,
                config: { replace: true, state: createdRouteInfo?.state },
                formType: createdRouteInfo?.formType || unifiedBusinessFormType,
                utmPayload: data?.gTagUtmData
              });
            }
          }
        } else if (form?.formType == "BUSINESS_PRIME") {
          navigationWrapper({ url: `/${form?.path}/${response?.data?.userContent?.application_id}`, config: { replace: true }, formType: form?.formType, utmPayload: data?.gTagUtmData })
        } else if (form?.path && form?.apiPath) {
          const encodedEmail = encodeURIComponent(data?.email ?? "");
          const userFormResponse = await doGET(`/api/${form?.apiPath}/grid?email=${encodedEmail}`)
          const applicationFormResponse = await doGET(`/api/application-form/grid?email=${encodedEmail}&status=in[0,1,2,3,4,5,6,7]`)
          if (userFormResponse?.data?.rows?.length && applicationFormResponse?.data?.rows?.length) {
            const resolvedApplicationId = userFormResponse?.data?.rows[0]?._id;
            const gridResumePath = getValidatedResumePathForApplication({
              form,
              applicationId: resolvedApplicationId,
              requireApplicationId: true,
            });
            navigationWrapper({ url: gridResumePath || `/${form?.path}/${resolvedApplicationId}`, config: { replace: true }, formType: form?.formType, utmPayload: data?.gTagUtmData })
          } else {
            const userFormCreateResponse = await doPOST(
              `/api/${form?.apiPath}/create?${buildCreateQueryString(data)}`,
              {
                email: data.email,
                ...(form?.isPrime && { interestedInPrime: true })
              }
            )
            if (userFormCreateResponse?.data?._id) {
              navigationWrapper({ url: `/${form?.path}/${userFormCreateResponse?.data?._id}`, config: { replace: true }, formType: form?.formType, utmPayload: data?.gTagUtmData })
            }
          }
        }
      } else {
        showError(response)
        setStep(0);
      }

    } catch (e) {
      const createPayload = isUnifiedBusinessLogin
        ? { email: data.email, type: unifiedBusinessType }
        : { email: data.email };
      const userFormCreateResponse = await doPOST(`/api/${form?.apiPath}/create?${buildCreateQueryString(data)}`, createPayload)
      if (userFormCreateResponse?.data?._id) {
        const createdRouteInfo = isUnifiedBusinessLogin
          ? await resolveBusinessRouteInfo({
            applicationId: userFormCreateResponse?.data?._id,
            fallbackType: unifiedBusinessType,
            form,
          })
          : null;
        navigationWrapper({
          url: createdRouteInfo?.url || `/${form?.path}/${userFormCreateResponse?.data?._id}`,
          config: { replace: true, state: createdRouteInfo?.state },
          formType: createdRouteInfo?.formType || unifiedBusinessFormType || form?.formType,
          utmPayload: data?.gTagUtmData
        })
      }

      setStep(0);
      try {
        showError(JSON?.parse(e?.message)?.message ?? e);
      } catch (error) {
        showError(e)
      }
    } finally {
      setLoading(false)
    }
  };

  useEffect(() => {
    document.body.className = "authentication-bg";
    const fetch = async () => {
      await populateUserContent();
      setData({ ...data, countryCode: userContent?.countryCode });
    };
    fetch();
    return function cleanup() {
      document.body.className = "";
    };
  }, []);

  if (eddSelection) {
    return (
      <EddWorkableRequestsScreen
        decision={eddSelection.decision}
        eddRequests={eddSelection.eddRequests}
        actionLoadingKey={eddActionLoadingKey}
        error={eddError}
        email={eddSelection.email}
        onResume={handleEddV2ResumeSelection}
        onBack={handleEddV2ChooserBack}
      />
    );
  }

  if (v2Selection) {
    return (
      <WorkableApplicationsScreen
        decision={v2Selection.decision}
        applications={v2Selection.applications}
        createOptions={v2Selection.createOptions}
        form={form}
        actionLoadingKey={v2ActionLoadingKey}
        error={v2Error}
        email={v2Selection.email}
        onResume={handleV2ResumeSelection}
        onCreate={handleV2CreateSelection}
        onBack={handleV2ChooserBack}
      />
    );
  }

  return (

    <div className="form-content-container" data-testid={otpTestId}>
      <div className='form-content-header' data-testid={buildAutomationChildTestId(otpTestId, "header")}>
        <img
          src={Logo}
          alt={t("fv-bank")}
          className="logo logo-dark form-content-logo"
          data-testid={buildAutomationChildTestId(otpTestId, "logo")}
        />
        <div className=''><LanguagePicker testId={buildAutomationChildTestId(testId, "language")} /></div>
      </div>

      <div className="form-content-body with-shadow compact " data-testid={buildAutomationChildTestId(otpTestId, "card")}>
        <div className='form-content-body-header' data-testid={buildAutomationChildTestId(testId, "title")}>
          {t(form?.name)}
        </div>
        <div className='form-content-body-content' data-testid={buildAutomationChildTestId(otpTestId, "content")}>
          <div className="form-content-body-content-desc" data-testid={buildAutomationChildTestId(otpTestId, "description")}>
            <div data-testid={buildAutomationChildTestId(otpTestId, "message")}>{t("We've sent a One Time Password to your email")}</div>
            <div data-testid={buildAutomationChildTestId(otpTestId, "instruction")}>{t("Please enter it below to continue")}</div>
          </div>
          <div className="form-horizontal " data-testid={buildAutomationChildTestId(otpTestId, "form")}>
            <div className="text-center mt-2">
              <div className="">
                <div className="d-flex align-items-center" data-testid={buildAutomationChildTestId(otpTestId, "email-section")}>
                  {data?.email && (
                    <InputField
                      id={buildAutomationChildTestId(testId, "email")}
                      value={data?.email}
                      label={t("Email Address")}
                      type="email"
                      showErr={true}
                      isDisabled={true}
                      className="col-12"
                      error={
                        data?.email?.length === 0
                          ? "Please enter Email"
                          : ""
                      }
                      inputFieldStyle={{ backgroundColor: "#F1FAFC", borderRadius: "14px", border: "1px solid #C6F3FF" }}
                    />
                  )}
                </div>
                <div className="d-flex  justify-content-center mt-3" data-testid={buildAutomationChildTestId(otpTestId, "code-section")}>
                  <Col style={{ minWidth: "200px", maxWidth: "300px" }} className="flex-1 m-0 p-0 col-md-6 col-12">
                    <OtpInput
                      testId={buildAutomationChildTestId(testId, "otp-code")}
                      value={data?.otpValue}
                      onChange={(v) => {
                        setData({ ...data, otpValue: v });
                      }}
                      error={
                        data?.otpValue?.length === 0
                          ? "please enter the OTP"
                          : ""
                      }
                      className="flex-1 mb-1 px-0"
                      style={{ borderRadius: "14px", border: "1px solid #C6F3FF", height: "50px", }} />
                  </Col>

                </div>
                {isDisabled ? (
                  <div className=" d-flex justify-content-end" data-testid={buildAutomationChildTestId(otpTestId, "resend-timer")}>
                    <Timer
                      setDisable={setIsDisabled}
                      initialMinute={0}
                      initialSeconds={20}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            <div className="mt-4 w-100 ">
              <ButtonGroupNew
                onNextClick={() => {
                  if (form?.formType == "EDD") {
                    handleEddLogin()
                    return;
                  }
                  onLogin();
                }}
                onBackClick={() => setStep(0)}
                step={2}
                asLabel={false}
                testId={buildAutomationChildTestId(testId, "otp-actions")}
                primaryActionTestIdSuffix="verify-otp"
                isDisabled={loading || data?.otpValue?.length != 4}
                loading={loading}
                backOpen
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 text-center" data-testid={buildAutomationChildTestId(testId, "version")}>
        <p className="font-size-16">
          Version 1.0.0
        </p>
      </div>
    </div>
  );
};

export default LoginOtp;
